/**
 * Input Validators for DMS Enterprise Platform Frontend
 */

const VALID_TLDS = new Set([
  'com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'io', 'co', 'in', 'us', 'uk', 'ca', 'de', 'jp', 'fr',
  'au', 'cn', 'ru', 'ch', 'nl', 'se', 'no', 'es', 'it', 'br', 'local', 'internal', 'lan', 'cloud', 'dev',
  'app', 'tech', 'site', 'online', 'store', 'info', 'biz', 'localhost', 'amazonaws', 'gcp', 'azure', 'postgres', 'mysql'
]);

/**
 * Validates Connection Name or Migration Job Name.
 * Only letters, numbers, spaces, hyphens (-), and underscores (_) are allowed.
 */
export function validateName(name, fieldLabel = 'Name') {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: `${fieldLabel} is required.` };
  }
  const trimmed = name.trim();
  if (trimmed.length < 2) {
    return { valid: false, error: `${fieldLabel} must be at least 2 characters.` };
  }
  if (trimmed.length > 100) {
    return { valid: false, error: `${fieldLabel} cannot exceed 100 characters.` };
  }

  // Regex for alphanumeric, spaces, hyphens, and underscores ONLY
  const allowedRegex = /^[a-zA-Z0-9\s_-]+$/;
  if (!allowedRegex.test(trimmed)) {
    return {
      valid: false,
      error: `Invalid ${fieldLabel.toLowerCase()}: Only letters, numbers, spaces, hyphens (-), and underscores (_) are allowed.`
    };
  }
  return { valid: true };
}

/**
 * Validates Host endpoint or IP address.
 * Rejects incomplete/invalid IPs (like 12.0) and dummy domain names (like abc.asd).
 */
export function validateHost(host) {
  if (!host || typeof host !== 'string') {
    return { valid: false, error: 'Host endpoint / IP is required.' };
  }
  const trimmed = host.trim();
  if (trimmed.length < 2) {
    return { valid: false, error: 'Host endpoint must be at least 2 characters.' };
  }
  if (trimmed.length > 255) {
    return { valid: false, error: 'Host endpoint cannot exceed 255 characters.' };
  }

  // Check if string consists of digits, dots, colons (IP attempt)
  const isNumericIpPattern = /^[\d\.\:]+$/.test(trimmed);
  if (isNumericIpPattern) {
    // Valid IPv4 check (4 octets, 0-255 each)
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipv4Regex.test(trimmed)) {
      return {
        valid: false,
        error: 'Invalid IP address. A valid IPv4 requires 4 octets between 0-255 (e.g., 192.168.1.1 or 13.127.60.98).'
      };
    }
    return { valid: true };
  }

  // Hostname / FQDN syntax check
  // Labels separated by dots, each label 1-63 chars, alphanumeric + hyphens, not starting/ending with hyphen.
  const hostnameRegex = /^(?=.{1,253}$)(?:(?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.)*(?:(?!-)[a-zA-Z0-9-]{1,63}(?<!-))$/;
  if (!hostnameRegex.test(trimmed) || trimmed.includes('..')) {
    return {
      valid: false,
      error: 'Invalid hostname format. Hostname cannot contain special characters or consecutive dots.'
    };
  }

  // Single-label hostnames without dots (e.g. afdewr, wfrwfwr) must be 'localhost'
  if (!trimmed.includes('.')) {
    if (trimmed.toLowerCase() !== 'localhost') {
      return {
        valid: false,
        error: `Invalid Host / IP endpoint "${trimmed}". Please enter a valid IPv4 address (e.g. 192.168.1.100) or FQDN host endpoint (e.g. db.example.com).`
      };
    }
  }

  // Dotted domain checks
  if (trimmed.includes('.')) {
    const parts = trimmed.split('.');
    const tld = parts[parts.length - 1].toLowerCase();

    // TLD cannot be purely numeric (e.g. host.123 or 12.0)
    if (/^\d+$/.test(tld)) {
      return { valid: false, error: 'Invalid host endpoint. Top-level domain cannot be numeric.' };
    }
    if (tld.length < 2) {
      return { valid: false, error: 'Invalid top-level domain in host endpoint.' };
    }

    // Check TLD against recognized TLD / internal extension list for 2-part domains (e.g. abc.asd)
    if (parts.length === 2 && !VALID_TLDS.has(tld) && tld.length <= 4) {
      return {
        valid: false,
        error: `Invalid top-level domain '.${tld}' in host endpoint. Please enter a valid hostname or IP address.`
      };
    }
  }

  return { valid: true };
}
